import { createSlice } from '@reduxjs/toolkit'
import { removeBoard } from '../shared/boardCleanup.actions'
import {
  createBoardRecurringTaskThunk,
  deleteBoardRecurringTaskThunk,
  fetchBoardRecurringTasksThunk,
  updateBoardRecurringTaskThunk,
} from './boardRecurringTasks.thunks'
import {
  createEmptyBoardRecurringTasksBoardState,
  createInitialBoardRecurringTasksState,
} from './boardRecurringTasks.state.utils'

const ensureBoard = (
  state: ReturnType<typeof createInitialBoardRecurringTasksState>,
  boardId: number,
) => {
  if (!state.byBoardId[boardId]) {
    state.byBoardId[boardId] = createEmptyBoardRecurringTasksBoardState()
  }

  return state.byBoardId[boardId]
}

const boardRecurringTasksSlice = createSlice({
  name: 'taskTrackerBoardRecurringTasks',
  initialState: createInitialBoardRecurringTasksState(),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(removeBoard, (state, action) => {
        delete state.byBoardId[action.payload.boardId]
      })
      .addCase(fetchBoardRecurringTasksThunk.pending, (state, action) => {
        const boardState = ensureBoard(state, action.meta.arg)
        boardState.loading = true
        boardState.error = null
      })
      .addCase(fetchBoardRecurringTasksThunk.fulfilled, (state, action) => {
        const boardState = ensureBoard(state, action.payload.boardId)
        boardState.items = action.payload.items
        boardState.loading = false
        boardState.error = null
      })
      .addCase(fetchBoardRecurringTasksThunk.rejected, (state, action) => {
        const payload = action.payload as { boardId: number; error: string }
        const boardState = ensureBoard(state, payload.boardId)
        boardState.loading = false
        boardState.error = payload.error
        boardState.items = []
      })
      .addCase(createBoardRecurringTaskThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).saving = true
      })
      .addCase(createBoardRecurringTaskThunk.fulfilled, (state, action) => {
        const boardState = ensureBoard(state, action.payload.boardId)
        boardState.saving = false
        boardState.items = [...boardState.items, action.payload.item].sort(
          (a, b) => a.id - b.id,
        )
      })
      .addCase(createBoardRecurringTaskThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).saving = false
      })
      .addCase(updateBoardRecurringTaskThunk.pending, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).saving = true
      })
      .addCase(updateBoardRecurringTaskThunk.fulfilled, (state, action) => {
        const boardState = ensureBoard(state, action.payload.boardId)
        boardState.saving = false
        boardState.items = boardState.items
          .map((item) =>
            item.id === action.payload.item.id ? action.payload.item : item,
          )
          .sort((a, b) => a.id - b.id)
      })
      .addCase(updateBoardRecurringTaskThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).saving = false
      })
      .addCase(deleteBoardRecurringTaskThunk.pending, (state, action) => {
        const boardState = ensureBoard(state, action.meta.arg.boardId)
        boardState.deletingId = action.meta.arg.recurringTaskId
      })
      .addCase(deleteBoardRecurringTaskThunk.fulfilled, (state, action) => {
        const boardState = ensureBoard(state, action.payload.boardId)
        boardState.deletingId = null
        boardState.items = boardState.items.filter(
          (item) => item.id !== action.payload.recurringTaskId,
        )
      })
      .addCase(deleteBoardRecurringTaskThunk.rejected, (state, action) => {
        ensureBoard(state, action.meta.arg.boardId).deletingId = null
      })
  },
})

export default boardRecurringTasksSlice.reducer
